import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { useState } from 'react'
import { Button } from '@/app/common/components/button/Button'
import { EditIconGeneral } from '@/common/icons/general.tsx'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  HelpLinkFormSchema,
  HelpLinkFormValues,
} from '@/pages/setup/link_handlers/help_links/help_links.schema.ts'
import { useUpdateHelpLink } from '@/queries/help_links/hooks.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'

interface Props {
  linkId: number
  currentLink: string
  onEditHelpLinkSuccess: () => void
  helpLinks: { link: string; id: number }[]
}

const EditHelpLinkPopup = (props: Props) => {
  const { onEditHelpLinkSuccess, helpLinks } = props
  const { toast } = useToast()

  const [open, setOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HelpLinkFormValues>({
    resolver: zodResolver(HelpLinkFormSchema),
    defaultValues: {
      link: helpLinks[0]?.link || '',
    },
  })

  const updateHelpLink = useUpdateHelpLink({
    onSuccess: () => {
      toast({
        title: 'Help Link Updated',
        description: 'The help link has been updated successfully.',
        variant: 'default',
      })
      onEditHelpLinkSuccess()
      setOpen(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `${error}`,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: HelpLinkFormValues) => {
    updateHelpLink.mutate({
      id: helpLinks[0]?.id ?? 0,
      data: {
        link: data.link,
      },
    })
  }

  const handleClose = () => {
    setOpen(false)
    reset({ link: 'null' })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value)
      }}
    >
      <DialogTrigger>
        <Button
          onClick={(event) => {
            setOpen(true)
            event.stopPropagation()
          }}
          variant={'outline'}
        >
          <EditIconGeneral />
        </Button>
      </DialogTrigger>
      <DialogContent onClick={(event) => event.stopPropagation()}>
        <h2 className="font-medium">Edit Help Link</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          <div>
            <label>Help Link</label>
            <Input
              type="text"
              {...register('link')}
              aria-invalid={errors.link ? 'true' : 'false'}
            />
            {errors.link && (
              <p className="text-sm text-red-500 mt-1">{errors.link.message}</p>
            )}
          </div>
          <div className="flex space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <div>
              <SubmitButton
                buttonTxt="Save changes"
                status={updateHelpLink.status}
              />
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default EditHelpLinkPopup
